import { Injectable } from "@angular/core";
import { Observable, catchError, forkJoin, map, of } from "rxjs";
import { environment } from "src/environments/environment.defaults";

import { DigitalEquipment, Purview, Road, Roadwork, RoadworkSection, User } from "../models";
import { RoadworkSegmentAssignment } from "../models/roadwork-segment-assignment.model";
import {
	DigitalEquipmentRequest,
	DigitalRoadworkDto,
	IdDto,
	LoginRequest,
	OpenApiClient,
	PointDto,
	RoadDto,
	RoadworkSectionDto,
} from "./bbo-client.service";
import { DigitalRoadworkDataFacade } from "./digital-roadwork-data.facade";
import { DigitalRoadworkMockDataService } from "./digital-roadwork-mock-data.service";
import { SnackBarService } from "./snackbar.service";

/**
 * The DigitalRoadworkDataService manages data calls.
 */
@Injectable({
	providedIn: "root",
	useClass: environment.useMockService ? DigitalRoadworkMockDataService : DigitalRoadworkDataService,
	deps: [OpenApiClient, SnackBarService],
})
export class DigitalRoadworkDataService implements DigitalRoadworkDataFacade {
	private readonly apiPrefix = "/api/v1";

	constructor(
		private readonly openApiClient: OpenApiClient,
		private readonly snackBarService: SnackBarService,
	) {}

	getRoadworks(): Observable<Roadwork[]> {
		return forkJoin({
			roadworks: this.openApiClient.roadworks(undefined, undefined, undefined),
			roads: this.openApiClient.roads(),
		}).pipe(
			map(({ roadworks, roads }) =>
				roadworks.map((roadwork) => {
					const correspondingRoad = roads.find((road) => road.roadCode === roadwork.route);
					return new Roadwork(roadwork, correspondingRoad ? new Road(correspondingRoad) : undefined);
				}),
			),
		);
	}

	getNearestRoadworks(assetId: string): Observable<Roadwork[]> {
		return forkJoin({
			roadworks: this.openApiClient.nearestRoadworks(assetId),
			roads: this.openApiClient.roads(),
		}).pipe(
			map(({ roadworks, roads }) =>
				roadworks.map((roadwork) => {
					const correspondingRoad = roads.find((road) => road.roadCode === roadwork.route);
					return new Roadwork(roadwork, correspondingRoad ? new Road(correspondingRoad) : undefined);
				}),
			),
		);
	}

	getNearDigitalEquipmentsNotBelongingToOtherRoadworks(
		latitude: number,
		longitude: number,
		roadworkId: string,
	): Observable<DigitalEquipment[]> {
		const request = new DigitalEquipmentRequest({
			points: [new PointDto({ lat: latitude, lon: longitude })],
			roadworkid: roadworkId,
		});
		return this.openApiClient
			.near(request)
			.pipe(
				map((digitalEquipmentDtos) => digitalEquipmentDtos.map((digitalEquipmentDto) => new DigitalEquipment(digitalEquipmentDto))),
			);
	}

	getAllDigitalEquipments(): Observable<DigitalEquipment[]> {
		return this.openApiClient
			.all()
			.pipe(
				map((digitalEquipmentDtos) => digitalEquipmentDtos.map((digitalEquipmentDto) => new DigitalEquipment(digitalEquipmentDto))),
			);
	}

	getDigitalEquipment(assetId: string): Observable<DigitalEquipment> {
		return this.openApiClient.digitalEquipment(assetId).pipe(map((digitalEquipmentDto) => new DigitalEquipment(digitalEquipmentDto)));
	}

	getDigitalRoadwork(roadworkId: string): Observable<RoadworkSegmentAssignment[]> {
		return this.openApiClient
			.digitalRoadwork(roadworkId)
			.pipe(
				map((digitalRoadworDtos) =>
					digitalRoadworDtos.map((digitalRoadworkDto) => new RoadworkSegmentAssignment(digitalRoadworkDto)),
				),
			);
	}

	getPurviews(): Observable<Purview[]> {
		throw new Error("Method not implemented.");
	}

	refreshToken(): Observable<User> {
		return this.openApiClient.refreshtoken(undefined).pipe(map((userResponse) => new User(userResponse)));
	}

	getRoads(): Observable<RoadDto[]> {
		return this.openApiClient.roads();
	}

	getRoadworkSections(roadworkId: string): Observable<RoadworkSection[]> {
		return this.openApiClient
			.sections(roadworkId)
			.pipe(
				map((roadworkSectionDtos) =>
					roadworkSectionDtos
						.sort((a, b) => this.compareSections(a, b))
						.map((roadworkSectionDto) => new RoadworkSection(roadworkSectionDto)),
				),
			);
	}

	compareSections(a: RoadworkSectionDto, b: RoadworkSectionDto): number {
		if (!!a.beginMeter && !!b.beginMeter && !!a.endMeter) {
			if (a.beginMeter > a.endMeter) {
				return a.beginMeter < b.beginMeter ? 1 : -1;
			} else {
				return a.beginMeter < b.beginMeter ? -1 : 1;
			}
		}
		return 1;
	}

	putDigitalRoadworkUpdate(roadworkSegmentAssignments: DigitalRoadworkDto[]): Observable<RoadworkSegmentAssignment[] | null> {
		return this.openApiClient.update(roadworkSegmentAssignments).pipe(
			map((digitalRoadworkDtos) =>
				digitalRoadworkDtos.map((digitalRoadworkDto) => new RoadworkSegmentAssignment(digitalRoadworkDto)),
			),
			catchError((err) => {
				this.snackBarService.showError("roadwork.km-values-not-consistent-with-direction");
				console.log(err);
				return of(null);
			}),
		);
	}

	postDigitalRoadworkDisallowAutomaticCloseForLongTermRoadwork(roadworkId: string): Observable<IdDto> {
		return this.openApiClient.disallowAutomaticCloseForLongTermRoadwork(roadworkId);
	}
	finishDigitalRoadwork(roadworkId: string): Observable<IdDto> {
		return this.openApiClient.finish(roadworkId);
	}

	postLogin(username: string, password: string): Observable<User> {
		return this.openApiClient
			.login(undefined, new LoginRequest({ username: username, password: password }))
			.pipe(map((userResponse) => new User(userResponse)));
	}

	postLogout(): Observable<void> {
		return this.openApiClient.logout(undefined);
	}

	startDigitalRoadwork(roadworkSegmentAssignments: DigitalRoadworkDto[]): Observable<RoadworkSegmentAssignment[] | null> {
		return this.openApiClient.start(roadworkSegmentAssignments).pipe(
			map((digitalRoadworkDtos) =>
				digitalRoadworkDtos.map((digitalRoadworkDto) => new RoadworkSegmentAssignment(digitalRoadworkDto)),
			),
			catchError((err) => {
				this.snackBarService.showError("roadwork.km-values-not-consistent-with-direction");
				console.log(err);
				return of(null);
			}),
		);
	}

	unassignDigitalRoadwork(roadworkId: string): Observable<IdDto> {
		return this.openApiClient.unassign(roadworkId);
	}

	setDigitalRoadworkToUnassignable(roadworkId: string): Observable<IdDto> {
		return this.openApiClient.unassignable(roadworkId);
	}
}
